import type { FetchParams, PaginatedResponse } from "@nuxt/app";
import HttpFactory from "~/repository/factory";
import { ENDPOINTS } from "~/repository/endpoints";
import { createPublicRequestConfig } from "~/repository/authSession";
import type {
  AdminUserExportPayload,
  FeatureFlag,
  FeatureFlagCreatePayload,
  FeatureFlagGroupsPayload,
  FeatureFlagUpdatePayload,
  FeatureFlagUsersPayload,
  UserResponse,
} from "~/types/admin/auth";
import type {
  ViewAsLaunchResponse,
  ViewAsRedeemResponse,
} from "~/types/viewAs";

class AdminAuthModule extends HttpFactory {
  async getUsers(
    fetchParams: FetchParams,
  ): Promise<PaginatedResponse<UserResponse>> {
    return await this.get<PaginatedResponse<UserResponse>>(
      `${ENDPOINTS.ADMIN.AUTH.USERS}`,
      fetchParams,
    );
  }

  async getUser(id: number): Promise<UserResponse> {
    return await this.get<UserResponse>(`${ENDPOINTS.ADMIN.AUTH.USERS}${id}`);
  }

  async exportUsersCsv(payload: AdminUserExportPayload): Promise<Blob> {
    return await this.post<Blob>(
      ENDPOINTS.ADMIN.AUTH.USERS_EXPORT_CSV,
      payload,
      { responseType: "blob" },
    );
  }

  async launchViewAs(userId: number): Promise<ViewAsLaunchResponse> {
    return await this.post<ViewAsLaunchResponse>(
      `${ENDPOINTS.ADMIN.AUTH.VIEW_AS_LAUNCH}`,
      { user_id: userId },
    );
  }

  async redeemViewAs(launchToken: string): Promise<ViewAsRedeemResponse> {
    return await this.post<ViewAsRedeemResponse>(
      `${ENDPOINTS.ADMIN.AUTH.VIEW_AS_REDEEM}`,
      { launch_token: launchToken },
      createPublicRequestConfig(),
    );
  }

  async getFeatureFlags(
    fetchParams?: FetchParams,
  ): Promise<PaginatedResponse<FeatureFlag> | FeatureFlag[]> {
    return await this.get<PaginatedResponse<FeatureFlag> | FeatureFlag[]>(
      `${ENDPOINTS.ADMIN.AUTH.FEATURE_FLAGS}`,
      fetchParams || {},
    );
  }

  async createFeatureFlag(
    payload: FeatureFlagCreatePayload,
  ): Promise<FeatureFlag> {
    return await this.post(`${ENDPOINTS.ADMIN.AUTH.FEATURE_FLAGS}`, payload);
  }

  async updateFeatureFlag(
    flagId: number | string,
    payload: FeatureFlagUpdatePayload,
  ): Promise<FeatureFlag> {
    return await this.patch(
      `${ENDPOINTS.ADMIN.AUTH.FEATURE_FLAGS}${flagId}/`,
      payload,
    );
  }

  async addFeatureFlagUsers(
    flagId: number | string,
    payload: FeatureFlagUsersPayload,
  ): Promise<FeatureFlag> {
    return await this.post(
      `${ENDPOINTS.ADMIN.AUTH.FEATURE_FLAGS}${flagId}/add-users/`,
      payload,
    );
  }

  async removeFeatureFlagUsers(
    flagId: number | string,
    payload: FeatureFlagUsersPayload,
  ): Promise<FeatureFlag> {
    return await this.post(
      `${ENDPOINTS.ADMIN.AUTH.FEATURE_FLAGS}${flagId}/remove-users/`,
      payload,
    );
  }

  async addFeatureFlagGroups(
    flagId: number | string,
    payload: FeatureFlagGroupsPayload,
  ): Promise<FeatureFlag> {
    return await this.post(
      `${ENDPOINTS.ADMIN.AUTH.FEATURE_FLAGS}${flagId}/add-groups/`,
      payload,
    );
  }

  async removeFeatureFlagGroups(
    flagId: number | string,
    payload: FeatureFlagGroupsPayload,
  ): Promise<FeatureFlag> {
    return await this.post(
      `${ENDPOINTS.ADMIN.AUTH.FEATURE_FLAGS}${flagId}/remove-groups/`,
      payload,
    );
  }
}

export default AdminAuthModule;
